import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { trackEvent } from "../../firebase/analytics";
import constructionImg from "../../tools/industryimages/con3.webp";
import retailImg from "../../tools/industryimages/retail1.webp";
import manufactureImg from "../../tools/industryimages/manu1.webp";
import restaurantImg from "../../tools/industryimages/rest3.webp";
import securityImg from "../../tools/industryimages/security1.webp";
import healthcareImg from "../../tools/industryimages/health1.webp";
import officeImg from "../../tools/industryimages/office1.webp";
import cleaningImg from "../../tools/industryimages/cleaning1.webp";
import heroLaptop from "../../tools/industryimages/oiikkolaptop.webp";
import "../../styles/Industries.css";

import {
  Utensils,
  HardHat,
  ShoppingBag,
  ShieldCheck,
  Hospital,
  Briefcase,
  Truck,
  Building2,
  Users,
  Factory,
  Hotel,
  GraduationCap,
  BarChart3,
  Clock,
} from "lucide-react";

const industries = [
  {
    key: "restaurants",
    title: "Restaurants",
    description:
      "Simplify shift scheduling, track attendance, manage overtime, and keep operations running smoothly.",
    image: restaurantImg,
    icon: Utensils,
  },
  {
    key: "construction",
    title: "Construction",
    description:
      "Track crews, manage sites, control labor costs, and keep projects on schedule.",
    image: constructionImg,
    icon: HardHat,
  },
  {
    key: "retail",
    title: "Retail",
    description:
      "Manage store staffing, peak-hour coverage, time tracking, and team visibility.",
    image: retailImg,
    icon: ShoppingBag,
  },
  {
    key: "security",
    title: "Security",
    description:
      "Manage guard schedules, track patrols, monitor attendance, and improve response visibility.",
    image: securityImg,
    icon: ShieldCheck,
  },
  {
    key: "healthcare",
    title: "Healthcare",
    description:
      "Manage staff schedules, track shifts, ensure compliance, and improve patient care operations.",
    image: healthcareImg,
    icon: Hospital,
  },
  {
    key: "cleaning",
    title: "Cleaning Services",
    description:
      "Manage cleaning crews, track job schedules, monitor attendance, and ensure service quality across locations.",
    image: cleaningImg,
    icon: Truck,
  },
  {
    key: "corporate",
    title: "Corporate Teams",
    description:
      "Streamline employee management, time tracking, and internal operations across departments.",
    image: officeImg,
    icon: Building2,
  },
  {
    key: "manufacturing",
    title: "Manufacturing",
    description:
      "Monitor workforce efficiency, track shifts, and optimize production schedules.",
    image: manufactureImg,
    icon: Factory,
  },
];

const industryDetails = {
  construction: {
    eyebrow: "Construction",
    title: "Workforce management built for construction teams",
    description:
      "Construction projects move fast. Oiiko helps you keep crews, sites, schedules, attendance, and labor visibility connected from start to finish.",
    image: constructionImg,
    features: [
      [
        "Crew Scheduling",
        "Assign crews to specific job sites and shifts without overlap or confusion.",
      ],
      [
        "On-Site Time Tracking",
        "Employees clock in from job sites so you know exactly who is on-site.",
      ],
      [
        "Task & Job Tracking",
        "Assign tasks per site with checklists and photo proof for completed work.",
      ],
      [
        "Labor Cost Visibility",
        "Track hours, overtime, and project labor costs in real time.",
      ],
    ],
  },
  restaurants: {
    eyebrow: "Restaurants",
    title: "Workforce management for restaurants",
    description:
      "Manage shifts, track attendance, control labor costs, and keep your team aligned during peak hours.",
    image: restaurantImg,
    features: [
      [
        "Shift Scheduling",
        "Build schedules around peak hours and avoid understaffing during rush.",
      ],
      [
        "Real-Time Attendance",
        "See who clocked in late or missed shifts instantly.",
      ],
      [
        "Task Management",
        "Assign opening, closing, and cleaning tasks with clear accountability.",
      ],
      [
        "Leave Management",
        "Handle time-off requests without disrupting shift coverage.",
      ],
    ],
  },
  healthcare: {
    eyebrow: "Healthcare",
    title: "Workforce management for healthcare teams",
    description:
      "Ensure proper staffing, track shifts, and maintain compliance across healthcare operations.",
    image: healthcareImg,
    features: [
      [
        "Shift Scheduling",
        "Manage rotating shifts and ensure proper staff coverage at all times.",
      ],
      [
        "Attendance Tracking",
        "Track clock-ins, lateness, and shift completion accurately.",
      ],
      [
        "Training Manager",
        "Assign compliance and certification training with deadlines.",
      ],

      [
        "Reports & Analytics",
        "Monitor staffing trends and ensure compliance across teams.",
      ],
    ],
  },
  retail: {
    eyebrow: "Retail",
    title: "Workforce management for retail teams",
    description:
      "Manage store staffing, peak-hour coverage, attendance, and team visibility across one or multiple locations.",
    image: retailImg,
    features: [
      [
        "Store Scheduling",
        "Schedule employees based on peak hours and store demand.",
      ],
      [
        "Attendance Tracking",
        "Know who is late, absent, or working across locations.",
      ],
      [
        "Task Manager",
        "Assign daily store tasks like restocking, cleaning, and audits.",
      ],
      [
        "Performance Insights",
        "Track employee hours and store-level productivity.",
      ],
    ],
  },

  security: {
    eyebrow: "Security",
    title: "Workforce management for security teams",
    description:
      "Manage guard schedules, track attendance, monitor coverage, and keep security operations organized.",
    image: securityImg,
    features: [
      [
        "Guard Scheduling",
        "Assign guards to posts, shifts, and locations with full visibility.",
      ],
      [
        "Live Attendance Tracking",
        "See which guards are on-site, late, or missing.",
      ],
      [
        "Incident & Task Tracking",
        "Log patrols, incidents, and assigned responsibilities.",
      ],

      ["Reports", "Track coverage gaps and improve response times."],
    ],
  },

  cleaning: {
    eyebrow: "Cleaning",
    title: "Workforce management for cleaning and janitorial teams",
    description:
      "Manage cleaning crews across multiple locations, track attendance, and ensure every job is completed on time and to standard.",
    image: cleaningImg,
    features: [
      [
        "Job Scheduling",
        "Assign crews to multiple locations and shifts easily.",
      ],
      [
        "Mobile Time Tracking",
        "Track when crews arrive, leave, and move between sites.",
      ],
      [
        "Task Checklists",
        "Standardize cleaning processes with step-by-step checklists.",
      ],
      ["Photo Proof", "Require proof of completed work for accountability."],
    ],
  },

  corporate: {
    eyebrow: "Corporate Teams",
    title: "Workforce management for corporate teams",
    description:
      "Streamline employee operations across departments with connected tools for time, scheduling, requests, and reporting.",
    image: officeImg,
    features: [
      [
        "Employee Management",
        "Keep all employee data organized and accessible.",
      ],
      ["Time Tracking", "Track work hours, attendance, and productivity."],
      [
        "Leave & Expense Management",
        "Handle requests and reimbursements in one place.",
      ],
      ["Training Manager", "Onboard employees and assign internal training."],
      [
        "Analytics & Reports",
        "Get insights into team performance and operations.",
      ],
    ],
  },

  manufacturing: {
    eyebrow: "Manufacturing",
    title: "Workforce management for manufacturing teams",
    description:
      "Track shifts, attendance, labor coverage, and workforce efficiency across production teams.",
    image: manufactureImg,
    features: [
      ["Shift Scheduling", "Plan production shifts based on demand and roles."],
      [
        "Attendance Tracking",
        "Monitor clock-ins, lateness, and shift completion.",
      ],

      ["Workforce Analytics", "Track efficiency and identify bottlenecks."],
      [
        "Payroll Reports",
        "Accurate shift-based reporting for payroll processing.",
      ],
    ],
  },
};

export default function Industries() {
  const [selectedIndustry, setSelectedIndustry] = useState("construction");
  const navigate = useNavigate();
  const activeDetails = industryDetails[selectedIndustry];
  const handleIndustryClick = (industry) => {
    trackEvent("industry_card_click", {
      industry,
    });

    setSelectedIndustry(industry);

    const section = document.getElementById("industry-details");
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <Navbar />

      <main className="industries-page">
        <section className="industries-hero industries-hero-split">
          <div className="industries-hero-copy">
            <span className="industries-eyebrow">Industries</span>
            <h1>Built for every industry. Designed for people.</h1>
            <p>
              Oiiko helps businesses manage teams more efficiently, stay
              organized, and keep daily operations moving without disconnected
              tools.
            </p>

            <div className="industries-proof-row">
              <div className="proof-item">
                <Users size={21} strokeWidth={2} />
                <p>Industry-specific workflows</p>
              </div>

              <div className="proof-item">
                <BarChart3 size={21} strokeWidth={2} />
                <p>Real-time visibility</p>
              </div>

              <div className="proof-item">
                <ShieldCheck size={21} strokeWidth={2} />
                <p>Compliance made simple</p>
              </div>

              <div className="proof-item">
                <Clock size={21} strokeWidth={2} />
                <p>Save time and reduce cost</p>
              </div>
            </div>
          </div>

          <div className="industries-hero-visual">
            <img
              src={heroLaptop}
              alt="Oiiko dashboard on laptop"
              className="industries-hero-image"
            />
          </div>
        </section>

        <section className="industries-grid-section">
          <h2>Industries We Serve</h2>
          <p>Solutions tailored to the way your industry works.</p>

          <div className="industries-grid">
            {industries.map((industry) => {
              const Icon = industry.icon;

              return (
                <button
                  type="button"
                  key={industry.key}
                  className={`industry-card ${
                    selectedIndustry === industry.key ? "active" : ""
                  }`}
                  onClick={() => handleIndustryClick(industry.key)}
                >
                  {industry.image && (
                    <img
                      src={industry.image}
                      alt={`${industry.title} industry`}
                      className="industry-card-image"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <div className="industry-card-content">
                    <div className="industry-icon">
                      <Icon size={26} strokeWidth={2} />
                    </div>

                    <h3>{industry.title}</h3>
                    <p>{industry.description}</p>
                    <span className="industry-link">View preview →</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
        <section id="industry-details" className="industry-detail-section">
          <div className="industry-detail-copy">
            <span className="industries-eyebrow">{activeDetails.eyebrow}</span>
            <h2>{activeDetails.title}</h2>
            <p>{activeDetails.description}</p>

            <div className="industry-feature-list">
              {activeDetails.features.map(([title, description]) => (
                <div key={title}>
                  <strong>{title}</strong>
                  <p>{description}</p>
                </div>
              ))}
            </div>

            <button
              className="industries-primary-btn"
              onClick={() => {
                trackEvent("industry_preview_learn_more_click", {
                  industry: selectedIndustry,
                  location: "detail_section",
                });

                navigate(`/industries/${selectedIndustry}`);
              }}
            >
              Learn more about {activeDetails.eyebrow.toLowerCase()} →
            </button>
          </div>

          <div className="industry-detail-visual">
            {activeDetails.image && (
              <img
                src={activeDetails.image}
                alt={`${activeDetails.eyebrow} workforce`}
                className="industry-detail-image"
                loading="lazy"
                decoding="async"
              />
            )}

            <div className="site-overview-card floating-stats-card">
              <h3>{activeDetails.eyebrow} Overview</h3>

              <div className="site-stats">
                <div>
                  <strong>4</strong>
                  <span>Active Teams</span>
                </div>
                <div>
                  <strong>128</strong>
                  <span>Employees</span>
                </div>
                <div>
                  <strong>96%</strong>
                  <span>Attendance</span>
                </div>
                <div>
                  <strong>12</strong>
                  <span>Late Today</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="industries-final-cta">
          <div>
            <h2>One platform. Every industry.</h2>
            <p>
              Oiiko adapts to your industry so you can focus on what matters
              most — your people and your business.
            </p>
          </div>

          <button
            onClick={() => {
              trackEvent("industries_final_cta_click", {
                destination: "/register",
              });
              navigate("/register");
            }}
          >
            Start for free now!
          </button>
        </section>
      </main>
    </>
  );
}
